// src/ui/shell/TeamLayout.tsx: the builder team's workspace shell. It is the family side's shell with the team's destinations: the same
// sidebar (the builder's logo, .nav-i rows, .nav-label sections, the .me row), the same 64px top bar with crumbs and actions, the
// same phone bar and tab bar (src/ui/shell/parts.tsx holds what both sides share). Only the accent differs (direction.css .app.team:
// homebuild's navy).
// Desktop (1024px and up): the builder's logo on top, then Home, Inbox, Messages, Families and Pipeline; "Your Business" (Inquiry Form,
// Starter Ideas, Our Homes); and the signed-in person, who opens Settings. No card under the logo repeats the builder's name, and
// there are no "Views": New, Ready and Held are the tabs of Inbox and Families. The top bar holds the crumbs ("Inbox",
// "Families > The Brennan Home") and the screen's actions.
// Phone and tablet: the builder's logo and the search and New Project buttons on top, a tab bar of Home, Inbox, Messages, Families
// and More; More lists Your Business (Pipeline first) and Settings. Screens render their own title and columns inside.
// Root class: hb-shell.
import { Fragment, type ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import { href, Link } from "../../app/router";
import { site, tenant } from "../../state/session";
import { IconButton } from "../Button";
import { cx } from "../cx";
import { Icon, type IconName } from "../Icon";
import { Badge } from "../Pill";
import { Sheet } from "../Sheet";
import { TabLink } from "./ProspectShell";
import { BarBrand, MeRow, NavItem, SideBrand } from "./parts";
import { TopBar, type Crumb } from "./TopBar";
import { DemoStrip, useShellMounted } from "./DemoStrip";
import { SkipLink } from "./SkipLink";

export type TeamSection = "home" | "inbox" | "messages" | "projects" | "pipeline" | "build" | "ideas" | "homes" | "settings";

export type TeamLayoutProps = {
  active: TeamSection | null;
  /** The nav counts: families waiting on a reply, threads with unread messages (nothing shows at 0). */
  counts?: { inbox?: number; unread?: number };
  me: { name: string; initials: string; title: string };
  /** Team admins see the Inquiry Form in the nav. */
  admin?: boolean;
  /** Desktop: the top bar's crumbs (default: the section's name). */
  crumbs?: readonly Crumb[];
  /** Desktop: the top bar's actions (the search and New Project on Inbox and Families). */
  actions?: ComponentChildren;
  /** Phone: opens search (the family list's search field). */
  onSearch?: () => void;
  /** Phone: opens New Project (a button in the top bar). */
  onNew?: () => void;
  banner?: ComponentChildren;
  class?: string;
  children?: ComponentChildren;
};

type NavDef = { id: TeamSection; to: string; icon: IconName; label: string; count?: "inbox" | "unread"; admin?: boolean };
type NavGroup = { label: string | null; items: NavDef[] };

/** The nav, top to bottom: every destination is a real route. */
export function navGroups(admin: boolean, preview = false): NavGroup[] {
  return [
    {
      label: null,
      items: [
        // In preview Home is the setup workspace, which lives at bare /team.
        { id: "home", to: preview ? href("team") : href("teamHome"), icon: "home", label: "Home" },
        { id: "inbox", to: href("team", {}, { view: "inbox" }), icon: "tray", label: "Inbox", count: "inbox" },
        { id: "messages", to: href("teamMessages"), icon: "msg", label: "Messages", count: "unread" },
        { id: "projects", to: href("team", {}, { view: "active" }), icon: "users", label: "Families" },
        { id: "pipeline", to: href("teamPipeline"), icon: "trend", label: "Pipeline" },
      ],
    },
    {
      label: "Your Business",
      items: [
        // The form families fill out on the builder's website; its route keeps the old name.
        ...(admin ? [{ id: "build" as const, to: href("teamStartYourBuild"), icon: "cursor" as const, label: "Inquiry Form" }] : []),
        { id: "ideas", to: href("teamIdeas"), icon: "bulb", label: "Starter Ideas" },
        { id: "homes", to: href("teamHomes"), icon: "image", label: "Our Homes" },
      ],
    },
  ];
}

export const TITLE: Record<TeamSection, string> = {
  home: "Home", inbox: "Inbox", messages: "Messages", projects: "Families", pipeline: "Pipeline",
  build: "Inquiry Form", ideas: "Starter Ideas", homes: "Our Homes", settings: "Settings",
};

const MORE_ORDER: readonly { label: string | null; ids: readonly TeamSection[] }[] = [
  { label: "Your Business", ids: ["pipeline", "build", "ideas", "homes"] },
  { label: null, ids: ["settings"] },
];

/** The foot's role line keeps room for "· Settings": a title's first part ("President, Co-Owner" → "President"). */
export function shortTitle(title: string): string {
  return title.split(/\s*[,;/(|]\s*/)[0]?.trim() || title;
}

export function TeamLayout(props: TeamLayoutProps) {
  const t = tenant.value;
  useShellMounted();
  const [more, setMore] = useState(false);
  const groups = navGroups(!!props.admin, site.value?.status === "setup");
  const flat = groups.flatMap((g) => g.items);
  const c = props.counts ?? {};
  const unread = c.unread ?? 0;
  // Messages carries the one dark badge (threads with unread messages), like Messages on the family side; Inbox's count is a plain .cnt.
  const badge = <Badge count={unread} label={`${unread} unread`} />;
  const onMore = !!props.active && !["home", "inbox", "messages", "projects"].includes(props.active);
  const settingsCur = props.active === "settings";
  const rail = (n: NavDef) => {
    const count = n.count ? (c[n.count] ?? 0) : 0;
    const badgeEl = !count ? null : n.count === "unread" ? badge : <span class="cnt">{count}</span>;
    return <NavItem key={n.id} to={n.to} icon={n.icon} label={n.label} current={props.active === n.id} badge={badgeEl} />;
  };
  const moreItem = (id: TeamSection): { to: string; icon: IconName; label: string } =>
    id === "settings" ? { to: href("teamSettings"), icon: "gear", label: "Settings" } : flat.find((n) => n.id === id)!;
  return (
    <div class={cx("hb-shell", "app", "team", "has-tabbar", props.class)} data-shell="team">
      <SkipLink />
      <aside class="side" aria-label="Team navigation">
        <SideBrand to={groups[0]!.items[0]!.to} />
        <nav class="nav" aria-label="Workspace">
          {groups[0]!.items.map((n) => rail(n))}
        </nav>
        {groups.slice(1).map((g) => (
          <Fragment key={g.label}>
            <div class="nav-label">
              <span>{g.label}</span>
            </div>
            <nav class="nav" aria-label={g.label ?? undefined}>
              {g.items.map((n) => rail(n))}
            </nav>
          </Fragment>
        ))}
        <div class="side-foot">
          <MeRow to={href("teamSettings")} current={settingsCur} initials={props.me.initials} tone="team" name={props.me.name} roleLabel={shortTitle(props.me.title)} />
        </div>
      </aside>

      <div class="main">
        <DemoStrip />
        {props.banner}
        <TopBar crumbs={props.crumbs ?? [{ label: props.active ? TITLE[props.active] : (t?.name ?? "") }]} actions={props.actions} />
        <header class="mtop">
          <BarBrand to={groups[0]!.items[0]!.to} />
          <div class="mtop-r">
            {props.onSearch ? <IconButton class="tm-search" icon="search" label="Search families" onClick={props.onSearch} /> : null}
            {props.onNew ? <IconButton class="tm-new" icon="plus" label="New Project" title="New Project" onClick={props.onNew} /> : null}
          </div>
        </header>
        {props.children}
      </div>

      <nav class="tabbar ttab" aria-label="Team" style={{ "--n": "5" }}>
        {groups[0]!.items.filter((n) => n.id !== "pipeline").map((n) => (
          <TabLink key={n.id} to={n.to} icon={n.icon} label={n.label} current={props.active === n.id} badge={n.id === "messages" ? badge : null} />
        ))}
        <button type="button" class={cx("tab", onMore && "is-active")} onClick={() => setMore(true)} aria-haspopup="dialog">
          <span class="ic">
            <Icon name="more" />
          </span>
          More
        </button>
      </nav>
      <Sheet open={more} onClose={() => setMore(false)} title="More" class="ts-more">
        <nav class="ts-more-list" aria-label="More">
          {MORE_ORDER.map((g, gi) => {
            const ids = g.ids.filter((id) => id !== "build" || props.admin);
            return (
              <div key={gi} class="ts-more-g" role="group" aria-label={g.label ?? undefined}>
                {g.label ? <div class="ts-more-h">{g.label}</div> : null}
                {ids.map((id) => {
                  const m = moreItem(id);
                  const cur = props.active === id;
                  return (
                    <Link key={id} class="ts-more-i" to={m.to} aria-current={cur ? "page" : undefined} onClick={() => setMore(false)}>
                      <Icon name={m.icon} filled={cur} />
                      <span>{m.label}</span>
                      <Icon name="chev-r" />
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </nav>
      </Sheet>
    </div>
  );
}
